import { useId, useState } from "react";

import { useSaveTextSnippet } from "../../hooks/useInvoices";
import TextbausteinBibliothek from "./TextbausteinBibliothek";
import { applySnippetPlaceholders } from "../../lib/invoiceEditor";
import {
  errorText,
  type SnippetListe,
  type TextSnippet,
  type WebInvoiceDraft,
} from "../../lib/invoices";

type Props = {
  /** Welche der vier Bausteinlisten — entscheidet, wohin gesichert wird. */
  liste: SnippetListe;
  titel: string;
  /** Steht ueber der Bausteinzeile — hier der Dokumenttitel. */
  vorspann?: React.ReactNode;
  /**
   * Was im zugeklappten Kopf steht. Ohne Angabe die erste Textzeile; mit
   * Vorspann ist der Dokumenttitel die aussagekraeftigere Zusammenfassung.
   */
  zusammenfassung?: string;
  value: string;
  snippets: TextSnippet[];
  onChange: (value: string) => void;
  draft: WebInvoiceDraft;
  /** Klappzustand beim ersten Rendern; die Karte selbst kommt von aussen. */
  children: (inhalt: React.ReactNode, zusammenfassung: string) => React.ReactElement;
};

/**
 * Einleitungs- oder Schlusstext eines Belegs, mit den Bausteinen dahinter.
 *
 * Drei Dinge in einer Bedienzeile, ohne die Karte zu überladen:
 *
 * 1. **Wählen** — durchsuchbares Feld statt Aufklappliste. Die Schlusstexte
 *    sind zu zwölft, und ab etwa sieben Einträgen ist eine `<select>`-Liste
 *    laut den UI-Konventionen die falsche Form.
 * 2. **Einfügen** ersetzt den Feldinhalt (Benjamins Entscheidung 07.09.2026).
 *    Damit ist immer eindeutig, welcher Baustein gerade im Feld steht — die
 *    Voraussetzung dafür, dass „sichern“ wahlweise aktualisieren heisst.
 *    Weil Ersetzen Getipptes verwirft, steht danach ein „Rückgängig“ bereit;
 *    ein Bestätigungsdialog vor jedem Einfügen wäre der lautere Weg gewesen.
 * 3. **Sichern** klappt eine zweite Zeile auf, statt einen zweiten Knopf
 *    danebenzustellen. Das Namensfeld ist mit dem zuletzt eingefügten
 *    Baustein vorbelegt und schlägt die vorhandenen Namen vor: bekannter Name
 *    = aktualisieren, neuer Name = neue Vorlage. Ein Bedienweg für beides,
 *    und die Zeile darunter sagt vorher, was passieren wird.
 *
 * Die Karte selbst (Klappverhalten, Kopf) kommt als `children`-Funktion von
 * der Editor-Seite — sie besitzt die `Klappkarte`, und die Zusammenfassung im
 * Kopf hängt am Textinhalt, den diese Komponente führt.
 */
export default function InvoiceTextKarte({
  liste, titel, vorspann, zusammenfassung, value, snippets, onChange, draft, children,
}: Props) {
  const [gewaehlt, setGewaehlt] = useState("");
  /** Name des zuletzt eingefügten Bausteins — Vorbelegung fürs Sichern. */
  const [herkunft, setHerkunft] = useState("");
  /** Feldinhalt vor dem letzten Einfügen; leer = nichts zurückzunehmen. */
  const [vorherigerText, setVorherigerText] = useState<string | null>(null);
  const [sichernOffen, setSichernOffen] = useState(false);
  const [name, setName] = useState("");
  const [meldung, setMeldung] = useState("");
  /** Die ganze Bibliothek — umbenennen, löschen, Standard setzen. */
  const [bibliothekOffen, setBibliothekOffen] = useState(false);
  const sichern = useSaveTextSnippet();
  const listenId = useId();

  const einfuegen = () => {
    const baustein = snippets.find((eintrag) => eintrag.label === gewaehlt.trim());
    if (!baustein) return;
    setVorherigerText(value);
    setHerkunft(baustein.label);
    setMeldung("");
    onChange(applySnippetPlaceholders(
      baustein.text.trim(), draft.issue_date, draft.due_date, draft.payment_term_days,
    ));
  };

  const zurueck = () => {
    if (vorherigerText === null) return;
    onChange(vorherigerText);
    setVorherigerText(null);
    setHerkunft("");
  };

  const sichernOeffnen = () => {
    setName(herkunft);
    setMeldung("");
    setSichernOffen(true);
  };

  const bekannt = snippets.some(
    (eintrag) => eintrag.label.trim().toLowerCase() === name.trim().toLowerCase(),
  );

  const speichern = async () => {
    const label = name.trim();
    if (!label || !value.trim()) return;
    try {
      const ergebnis = await sichern.mutateAsync({ liste, label, text: value });
      setHerkunft(label);
      setSichernOffen(false);
      setMeldung(ergebnis.ersetzt
        ? `Baustein „${label}“ aktualisiert.`
        : `Baustein „${label}“ angelegt.`);
    } catch (fehler) {
      setMeldung(errorText(fehler));
    }
  };

  const ersteZeile = (value || "").split("\n").find((zeile) => zeile.trim()) || "noch kein Text";

  const inhalt = (
    <>
      {vorspann}
      <div className="invoice-snippet-row">
        <input
          list={listenId}
          value={gewaehlt}
          onChange={(event) => setGewaehlt(event.target.value)}
          placeholder="Textbaustein suchen …"
          aria-label={`Textbaustein für ${titel}`}
        />
        <datalist id={listenId}>
          {snippets.map((eintrag) => <option value={eintrag.label} key={eintrag.label} />)}
        </datalist>
        <button
          className="invoice-button"
          type="button"
          onClick={einfuegen}
          disabled={!snippets.some((eintrag) => eintrag.label === gewaehlt.trim())}
        >
          Einfügen
        </button>
        <button
          className="invoice-button invoice-button-quiet invoice-snippet-sichern"
          type="button"
          onClick={() => (sichernOffen ? setSichernOffen(false) : sichernOeffnen())}
          disabled={!value.trim()}
          aria-expanded={sichernOffen}
        >
          Sichern …
        </button>
        {/* Der kurze Weg in die ganze Liste: umbenennen, löschen und den
            Standard festlegen geht hier nicht, und dafür den Beleg zu
            verlassen wäre der teuerste Weg zur kleinsten Änderung. Die
            Bibliothek öffnet sich mit genau dieser Liste vorgewählt. */}
        <button
          className="invoice-button invoice-button-quiet"
          type="button"
          onClick={() => setBibliothekOffen(true)}
          title="Alle Textbausteine verwalten"
        >
          Bibliothek
        </button>
      </div>

      {bibliothekOffen && (
        <TextbausteinBibliothek startListe={liste} onClose={() => setBibliothekOffen(false)} />
      )}

      {sichernOffen && (
        <div className="invoice-snippet-sichern-zeile">
          <label className="invoice-editor-field compact">
            <span>Name des Bausteins</span>
            <input
              list={`${listenId}-namen`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="z. B. Zahlung nach Abschluss"
              autoFocus
            />
            <datalist id={`${listenId}-namen`}>
              {snippets.map((eintrag) => <option value={eintrag.label} key={eintrag.label} />)}
            </datalist>
          </label>
          <button
            className="invoice-button primary"
            type="button"
            onClick={speichern}
            disabled={!name.trim() || sichern.isPending}
          >
            {sichern.isPending ? "Speichert …" : "Speichern"}
          </button>
          <button
            className="invoice-button"
            type="button"
            onClick={() => setSichernOffen(false)}
          >
            Abbrechen
          </button>
          <p className="invoice-editor-hint">
            {!name.trim()
              ? "Ohne Namen lässt sich der Baustein nicht wiederfinden."
              : bekannt
                ? `Überschreibt den vorhandenen Baustein „${name.trim()}“.`
                : `Legt „${name.trim()}“ als neue Vorlage an.`}
          </p>
        </div>
      )}

      {meldung && <p className="invoice-inline-note" role="status">{meldung}</p>}

      <textarea
        className="invoice-editor-longtext"
        rows={5}
        value={value}
        onChange={(event) => { onChange(event.target.value); setVorherigerText(null); }}
        placeholder="Absätze mit einer Leerzeile trennen."
      />

      {vorherigerText !== null && (
        <p className="invoice-inline-note" role="status">
          Text durch den Baustein ersetzt.{" "}
          <button type="button" className="invoice-linkbutton" onClick={zurueck}>Rückgängig</button>
        </p>
      )}
    </>
  );

  return children(inhalt, zusammenfassung?.trim() || ersteZeile);
}
